import { Vector3 } from 'three';
import { AttributeTypeENUM } from '../../enums/ProductEnums';
import { isValidEnumValue } from '../../utils';
import {
  AttributeDTO,
  FlipChangeDTO,
  MaterialChangeDTO,
  ModelChangeDTO,
  NumericChangeDTO,
  VariationChangeDTO,
} from '../DTOs/AttributeDTO';
import { AttributeValue, FlipValue, MaterialValue, ModelValue, VariationValue } from './AttributeValue';
import { LocalizedText } from './LocalizedText';
import { IdMapper, ProductBlueprint } from './ProductBlueprint';
import { MaterialValueDTO } from '../DTOs/AttributeValueDTO';
import { ReceiptSettings } from '../DTOs/ProductBlueprintDTO';

export interface ProductBlueprintAttributePair {
  productBlueprint: ProductBlueprint;
  attribute: Attribute;
}
export class Attribute {
  public static getEmptyAttribute() {
    return new Attribute(
      '',
      [],
      undefined,
      [new LocalizedText('en', '')],
      false,
      '',
      [new LocalizedText('en', '')],
      AttributeTypeENUM.BaseAttribute,
      false,
    );
  }

  public static fromDTO(
    attributeDTO: AttributeDTO,
  ): MaterialChange | VariationChange | ModelChange | NumericChange | Attribute | FlipChange {
    if (!isValidEnumValue(AttributeTypeENUM, attributeDTO.type)) {
      attributeDTO.type = AttributeTypeENUM.BaseAttribute;
    }

    const attributeValuesMap: Map<string, AttributeValue> = new Map();

    for (const attributeValueDTO of attributeDTO.attributeValues) {
      const attributeValue = AttributeValue.fromDTO(attributeValueDTO, attributeDTO.type);
      attributeValuesMap.set(attributeValue.id, attributeValue);
    }

    let defaultAttributeValue: number | AttributeValue | undefined =
      attributeDTO.defaultValue !== null && attributeDTO.defaultValue !== undefined
        ? attributeValuesMap.get(attributeDTO.defaultValue.toString())
        : undefined;
    if (!defaultAttributeValue && attributeDTO.type === AttributeTypeENUM.NumericChange)
      defaultAttributeValue = attributeDTO.defaultValue;

    const attributeIds = attributeDTO.attributeIds ? attributeDTO.attributeIds.map((id) => id.toString()) : [];

    switch (attributeDTO.type) {
      case AttributeTypeENUM.MaterialChange: {
        const materialChangeDTO = attributeDTO as MaterialChangeDTO;
        let absoluteTextureVector: Vector3 | undefined = undefined;

        if (materialChangeDTO.absoluteTextureVector) {
          if (
            materialChangeDTO.absoluteTextureVector[0] !== undefined &&
            materialChangeDTO.absoluteTextureVector[1] !== undefined &&
            materialChangeDTO.absoluteTextureVector[2] !== undefined
          ) {
            absoluteTextureVector = new Vector3(
              materialChangeDTO.absoluteTextureVector[0],
              materialChangeDTO.absoluteTextureVector[1],
              materialChangeDTO.absoluteTextureVector[2],
            );
          }
        }

        return new MaterialChange(
          materialChangeDTO.id.toString(),
          [...attributeValuesMap.values()] as MaterialValue[],
          defaultAttributeValue instanceof AttributeValue ? defaultAttributeValue : undefined,
          materialChangeDTO.infoText
            ? materialChangeDTO.infoText.map((infotext) => LocalizedText.fromDTO(infotext))
            : [],
          materialChangeDTO.multiSelection,
          materialChangeDTO.externalId,
          materialChangeDTO.name ? materialChangeDTO.name.map((name) => LocalizedText.fromDTO(name)) : [],
          materialChangeDTO.type,
          materialChangeDTO.isShown,
          materialChangeDTO.materialNames,
          materialChangeDTO.builderData,
          attributeIds,
          absoluteTextureVector,
          materialChangeDTO.isGlobal,
          materialChangeDTO.displayParameters,
        );
      }

      case AttributeTypeENUM.NumericChange: {
        const numericChangeDTO = attributeDTO as NumericChangeDTO;
        const defaultVal =
          attributeDTO.defaultValue !== null && attributeDTO.defaultValue !== undefined
            ? parseFloat(attributeDTO.defaultValue.toString())
            : 1;
        numericChangeDTO.unit = numericChangeDTO.unit ? numericChangeDTO.unit : 'm';
        numericChangeDTO.unitFactor = numericChangeDTO.unitFactor ? numericChangeDTO.unitFactor : 1;

        return new NumericChange(
          numericChangeDTO.id.toString(),
          defaultVal,
          numericChangeDTO.infoText ? numericChangeDTO.infoText.map((infotext) => LocalizedText.fromDTO(infotext)) : [],
          numericChangeDTO.multiSelection,
          numericChangeDTO.externalId,
          numericChangeDTO.name ? numericChangeDTO.name.map((name) => LocalizedText.fromDTO(name)) : [],
          numericChangeDTO.type,
          numericChangeDTO.isShown,
          numericChangeDTO.step,
          numericChangeDTO.max,
          numericChangeDTO.min,
          numericChangeDTO.unit,
          numericChangeDTO.unitFactor,
          numericChangeDTO.builderData,
          attributeIds,
          numericChangeDTO.isGlobal,
          numericChangeDTO.displayParameters,
        );
      }

      case AttributeTypeENUM.VariationChange: {
        const variationChangeDTO = attributeDTO as VariationChangeDTO;
        return new VariationChange(
          variationChangeDTO.id.toString(),
          [...attributeValuesMap.values()] as VariationValue[],
          defaultAttributeValue instanceof AttributeValue ? defaultAttributeValue : undefined,
          variationChangeDTO.infoText
            ? variationChangeDTO.infoText.map((infotext) => LocalizedText.fromDTO(infotext))
            : [],
          variationChangeDTO.multiSelection,
          variationChangeDTO.externalId,
          variationChangeDTO.name ? variationChangeDTO.name.map((name) => LocalizedText.fromDTO(name)) : [],
          variationChangeDTO.type,
          variationChangeDTO.isShown,
          variationChangeDTO.builderData,
          attributeIds,
          variationChangeDTO.isGlobal,
          variationChangeDTO.displayParameters,
        );
      }

      case AttributeTypeENUM.ModelChange: {
        const modelChangeDTO = attributeDTO as ModelChangeDTO;

        return new ModelChange(
          modelChangeDTO.id.toString(),
          [...attributeValuesMap.values()] as ModelValue[],
          defaultAttributeValue instanceof AttributeValue ? defaultAttributeValue : undefined,
          modelChangeDTO.infoText ? modelChangeDTO.infoText.map((infotext) => LocalizedText.fromDTO(infotext)) : [],
          modelChangeDTO.multiSelection,
          modelChangeDTO.externalId,
          modelChangeDTO.name ? modelChangeDTO.name.map((name) => LocalizedText.fromDTO(name)) : [],
          modelChangeDTO.type,
          modelChangeDTO.isShown,
          modelChangeDTO.visibilityChange,
          modelChangeDTO.builderData,
          attributeIds,
          modelChangeDTO.isGlobal,
          modelChangeDTO.displayParameters,
        );
      }

      case AttributeTypeENUM.BaseAttribute: {
        return new Attribute(
          attributeDTO.id.toString(),
          [...attributeValuesMap.values()],
          defaultAttributeValue,
          attributeDTO.infoText ? attributeDTO.infoText.map((infotext) => LocalizedText.fromDTO(infotext)) : [],
          attributeDTO.multiSelection,
          attributeDTO.externalId,
          attributeDTO.name ? attributeDTO.name.map((name) => LocalizedText.fromDTO(name)) : [],
          attributeDTO.type,
          attributeDTO.isShown,
          attributeDTO.builderData,
          attributeIds,
          attributeDTO.isGlobal,
          attributeDTO.displayParameters,
        );
      }
      case AttributeTypeENUM.FlipChange: {
        const flipChangeDTO = attributeDTO as FlipChangeDTO;
        return new FlipChange(
          flipChangeDTO.id.toString(),
          [...attributeValuesMap.values()] as FlipValue[],
          defaultAttributeValue instanceof AttributeValue ? defaultAttributeValue : undefined,
          flipChangeDTO.infoText ? flipChangeDTO.infoText.map((infotext) => LocalizedText.fromDTO(infotext)) : [],
          flipChangeDTO.multiSelection,
          flipChangeDTO.externalId,
          flipChangeDTO.name ? flipChangeDTO.name.map((name) => LocalizedText.fromDTO(name)) : [],
          flipChangeDTO.type,
          flipChangeDTO.isShown,
          flipChangeDTO.builderData,
          attributeIds,
          flipChangeDTO.isGlobal,
          flipChangeDTO.displayParameters,
        );
      }
    }
  }

  protected _id: string;
  protected _defaultValue?: AttributeValue | number;
  protected _infoText: LocalizedText[];
  protected _multiSelection: boolean;
  protected _externalId: string;
  protected _name: LocalizedText[];
  protected _type: AttributeTypeENUM;
  protected _isShown: boolean;
  protected _referenceAttributes: ProductBlueprintAttributePair[] = [];
  protected _referenceAttributesMap: Map<Attribute, ProductBlueprintAttributePair> = new Map<
    Attribute,
    ProductBlueprintAttributePair
  >();

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  protected _builderData: any = null;
  protected _attributeValues: AttributeValue[];
  protected _attributeIds: string[] = [];
  protected _attributes: Attribute[] = [];
  protected _attributesMap: Map<string, Attribute> = new Map<string, Attribute>();

  protected _isGlobal = false;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  protected _displayParameters: any = null;

  protected _receiptSettings?: ReceiptSettings;

  public get id() {
    return this._id;
  }

  public get externalId() {
    return this._externalId;
  }

  public set externalId(id) {
    this._externalId = id;
  }

  public get type() {
    return this._type;
  }

  public set type(t) {
    this._type = t;
  }

  public get defaultValue() {
    return this._defaultValue;
  }

  public get name() {
    return this._name;
  }

  public set name(n) {
    this._name = n;
  }

  public get infoText() {
    return this._infoText;
  }

  public set infoText(text) {
    this._infoText = text;
  }

  public get attributeValues() {
    return this._attributeValues;
  }

  public get multiSelection() {
    return this._multiSelection;
  }

  public set multiSelection(s) {
    this._multiSelection = s;
  }

  public get isShown() {
    return this._isShown;
  }

  public set isShown(s) {
    this._isShown = s;
  }

  public get builderData() {
    return this._builderData;
  }

  public get attributes() {
    return this._attributes;
  }

  public get referenceAttributes() {
    return this._referenceAttributes;
  }

  public get isGlobal() {
    return this._isGlobal;
  }

  public get receiptSettings() {
    return this._receiptSettings;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public get displayParameters(): any {
    return this._displayParameters;
  }

  constructor(
    id: string,
    attributeValues: AttributeValue[],
    defaultValue: AttributeValue | number | undefined,
    infoText: LocalizedText[],
    multiSelection: boolean,
    externalId: string,
    name: LocalizedText[],
    type: AttributeTypeENUM,
    isShown: boolean,
    builderData?: string,
    attributeIds?: string[],
    isGlobal = false,
    displayParameters?: string,
    receiptSettings?: ReceiptSettings,
  ) {
    this._id = id;
    this._attributeValues = attributeValues;
    this._defaultValue = defaultValue;
    this._infoText = infoText;
    this._multiSelection = multiSelection;
    this._externalId = externalId;
    this._name = name;
    this._type = type;
    this._isShown = isShown;
    if (builderData) {
      this._builderData = JSON.parse(builderData);
    }
    if (attributeIds) {
      this._attributeIds = attributeIds;
    }
    this._isGlobal = isGlobal;
    if (displayParameters) {
      this._displayParameters = JSON.parse(displayParameters);
    }
    this._receiptSettings = receiptSettings;
  }

  public toDTO() {
    return new AttributeDTO(
      Number(this._id),
      this._attributeValues.map((attributeValue) => attributeValue.toDTO()),
      this.defaultValue instanceof AttributeValue ? Number(this.defaultValue.id) : this.defaultValue,
      this.infoText.map((infoText) => infoText.toDTO()),
      this.multiSelection,
      this.externalId,
      this.name.map((name) => name.toDTO()),
      this.type,
      this.isShown,
      JSON.stringify(this.builderData),
      [...this._attributeIds].map((attributeId) => Number(attributeId)),
      this._isGlobal,
      this.displayParameters,
    );
  }

  public populateProductBlueprintReferences(availibleBlueprints: ProductBlueprint[], idMapper: IdMapper) {
    for (const attributeValue of this._attributeValues) {
      attributeValue.populateProductBlueprintReferences(availibleBlueprints, idMapper);
    }
  }

  public addReferenceAttribute(referenceAttribute: Attribute, productBlueprint: ProductBlueprint) {
    if (
      !this._referenceAttributes.find(
        (productBlueprintAttributePair) => productBlueprintAttributePair.attribute === referenceAttribute,
      )
    )
      this._referenceAttributes.push({ productBlueprint: productBlueprint, attribute: referenceAttribute });
  }

  public populateWithAttributes(
    availibleBlueprints: ProductBlueprint[],
    ownerBlueprint: ProductBlueprint,
    idMapper: IdMapper,
  ) {
    //preverimo, če lahko vse  spopuliramo preko mapa
    for (const referencedAttributeId of this._attributeIds) {
      if (this._attributes.find((attribute) => attribute.id === referencedAttributeId)) continue;
      const mappedAttribute = idMapper.attributeMap.get(referencedAttributeId);
      if (!mappedAttribute) continue;
      this._attributes.push(mappedAttribute.attribute);
      mappedAttribute.attribute.addReferenceAttribute(this, ownerBlueprint);
    }
    for (const productBlueprint of availibleBlueprints) {
      if (this._attributeIds.length === this._attributes.length) {
        break;
      }

      this._recursivelyPopulateWithAttributes(productBlueprint.attributes, ownerBlueprint, idMapper);
    }

    for (const attributeValue of this._attributeValues) {
      attributeValue.populateWithAttributeValues(availibleBlueprints, ownerBlueprint, idMapper);
    }
  }

  private _recursivelyPopulateWithAttributes(
    attributes: Attribute[],
    productBlueprint: ProductBlueprint,
    idMapper: IdMapper,
  ) {
    /* console.log(this._attributeIds.length === this._attributes.length); */

    for (const attribute of attributes) {
      //vsakič preverimo če smo že fertik
      if (this._attributeIds.length === this._attributes.length) {
        return;
      }
      //sproti zabeležujemo v map, ampak se verjetno zgodi ful večkrat kot potrebno
      idMapper.attributeMap.set(attribute.id, { attribute: attribute, productBlueprint: productBlueprint });

      if (
        this._attributeIds.includes(attribute.id) &&
        !this._attributes.find((storedAttribute) => storedAttribute.id === attribute.id)
      ) {
        this._attributes.push(attribute);
        attribute.addReferenceAttribute(this, productBlueprint);
      }
      //gremo rekurzivno dalje
      for (const attribteValue of attribute.attributeValues) {
        idMapper.attributeValueMap.set(attribteValue.id, attribteValue);
        this._recursivelyPopulateWithAttributes(attribteValue.attributes, productBlueprint, idMapper);
      }
    }
  }
}
export class VariationChange extends Attribute {
  constructor(
    id: string,
    attributeValues: VariationValue[],
    defaultValue: AttributeValue | undefined,
    infoText: LocalizedText[],
    multiSelection: boolean,
    externalId: string,
    name: LocalizedText[],
    type: AttributeTypeENUM,
    isShown: boolean,
    builderData?: string,
    attributeIds?: string[],
    isGlobal = false,
    displayParameters?: string,
  ) {
    super(
      id,
      attributeValues,
      defaultValue,
      infoText,
      multiSelection,
      externalId,
      name,
      type,
      isShown,
      builderData,
      attributeIds,
      isGlobal,
      displayParameters,
    );
  }
}
export class MaterialChange extends Attribute {
  public static override getEmptyAttribute() {
    return new MaterialChange(
      '',
      [],
      undefined,
      [new LocalizedText('en', '')],
      false,
      '',
      [new LocalizedText('en', '')],
      AttributeTypeENUM.MaterialChange,
      false,
      [],
    );
  }

  private _materialNames: string[];
  private _absoluteTextureVector?: Vector3;

  public get materialNames() {
    return this._materialNames;
  }

  public set materialNames(materialNames: string[]) {
    this._materialNames = materialNames;
  }

  public get absoluteTextureVector(): Vector3 | undefined {
    return this._absoluteTextureVector;
  }

  public set absoluteTextureVector(newVector: Vector3 | undefined) {
    this._absoluteTextureVector = newVector;
  }

  constructor(
    id: string,
    attributeValues: MaterialValue[],
    defaultValue: AttributeValue | undefined,
    infoText: LocalizedText[],
    multiSelection: boolean,
    externalId: string,
    name: LocalizedText[],
    type: AttributeTypeENUM,
    isShown: boolean,
    materialNames: string[],
    builderData?: string,
    attributeIds?: string[],
    absoluteTextureVector?: Vector3,
    isGlobal = false,
    displayParameters?: string,
  ) {
    super(
      id,
      attributeValues,
      defaultValue,
      infoText,
      multiSelection,
      externalId,
      name,
      type,
      isShown,
      builderData,
      attributeIds,
      isGlobal,
      displayParameters,
    );
    this._materialNames = materialNames;
    this._absoluteTextureVector = absoluteTextureVector;
  }

  public override toDTO() {
    const materialValueDTOs: MaterialValueDTO[] = [];

    for (const attributeValue of this.attributeValues) {
      if (attributeValue instanceof MaterialValue) {
        materialValueDTOs.push(attributeValue.toDTO());
      }
    }
    return new MaterialChangeDTO(
      Number(this._id),
      materialValueDTOs,
      this.defaultValue instanceof AttributeValue ? Number(this.defaultValue.id) : this.defaultValue,
      this.infoText.map((infoText) => infoText.toDTO()),
      this.multiSelection,
      this.externalId,
      this.name.map((name) => name.toDTO()),
      this.type,
      this.isShown,
      this.materialNames,
      this.builderData,
      [...this._attributeIds].map((attributeId) => Number(attributeId)),
      this._isGlobal,
      this.displayParameters,
      this.absoluteTextureVector
        ? [this.absoluteTextureVector.x, this.absoluteTextureVector.y, this.absoluteTextureVector.z]
        : [],
    );
  }
}

export class NumericChange extends Attribute {
  public static override getEmptyAttribute() {
    return new NumericChange(
      '',
      undefined,
      [new LocalizedText('en', '')],
      false,
      '',
      [new LocalizedText('en', '')],
      AttributeTypeENUM.NumericChange,
      true,
      0,
      0,
      0,
      '',
      1,
    );
  }

  private _step: number;
  private _max: number;
  private _min: number;
  private _unit: string;
  private _unitFactor: number;

  public get step() {
    return this._step;
  }

  public set step(s) {
    this._step = s;
  }

  public get max() {
    return this._max;
  }

  public set max(s) {
    this._max = s;
  }

  public get min() {
    return this._min;
  }

  public set min(s) {
    this._min = s;
  }

  public get unit() {
    return this._unit;
  }

  public get unitFactor() {
    return this._unitFactor;
  }

  constructor(
    id: string,
    defaultValue: AttributeValue | number | undefined,
    infoText: LocalizedText[],
    multiSelection: boolean,
    externalId: string,
    name: LocalizedText[],
    type: AttributeTypeENUM,
    isShown: boolean,
    step: number,
    max: number,
    min: number,
    unit: string,
    unitFactor: number,
    builderData?: string,
    attributeIds?: string[],
    isGlobal = false,
    displayParameters?: string,
  ) {
    super(
      id,
      [],
      defaultValue,
      infoText,
      multiSelection,
      externalId,
      name,
      type,
      isShown,
      builderData,
      attributeIds,
      isGlobal,
      displayParameters,
    );

    this._step = step;
    this._max = max;
    this._min = min;
    this._unit = unit;
    this._unitFactor = unitFactor;
  }
}

export class ModelChange extends Attribute {
  public static override getEmptyAttribute() {
    return new ModelChange(
      '',
      [],
      undefined,
      [new LocalizedText('en', '')],
      false,
      '',
      [new LocalizedText('en', '')],
      AttributeTypeENUM.ModelChange,
      false,
    );
  }

  private _visibilityChange: boolean;

  constructor(
    id: string,
    attributeValues: ModelValue[],
    defaultValue: AttributeValue | undefined,
    infoText: LocalizedText[],
    multiSelection: boolean,
    externalId: string,
    name: LocalizedText[],
    type: AttributeTypeENUM,
    isShown: boolean,
    visibilityChange = true,
    builderData?: string,
    attributeIds?: string[],
    isGlobal = false,
    displayParameters?: string,
  ) {
    super(
      id,
      attributeValues,
      defaultValue,
      infoText,
      multiSelection,
      externalId,
      name,
      type,
      isShown,
      builderData,
      attributeIds,
      isGlobal,
      displayParameters,
    );
    this._visibilityChange = visibilityChange;
  }

  public get visibilityChange() {
    return this._visibilityChange;
  }
}
export class FlipChange extends Attribute {
  constructor(
    id: string,
    attributeValues: FlipValue[],
    defaultValue: AttributeValue | undefined,
    infoText: LocalizedText[],
    multiSelection: boolean,
    externalId: string,
    name: LocalizedText[],
    type: AttributeTypeENUM,
    isShown: boolean,
    builderData?: string,
    attributeIds?: string[],
    isGlobal = false,
    displayParameters?: string,
  ) {
    super(
      id,
      attributeValues,
      defaultValue,
      infoText,
      multiSelection,
      externalId,
      name,
      type,
      isShown,
      builderData,
      attributeIds,
      isGlobal,
      displayParameters,
    );
  }
}
